import { Component } from "preact";
import React, { useEffect, useState } from "react";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";

import Header from "./header";

import {
  claimSet,
  claimSetEquivalent,
  Controllers,
  loggedInUser,
  menuItemContent,
  pageEntry,
  pageList,
  toClaimSet,
} from "../types";

import { Announcements } from "./announcements";
import prerenderData from "../../prerender-urls.json";
import prerenderMenu from "../../prerender-submenu.json";
import { Footer } from "./footer";
import { postLoginGoogle, postLoginToken } from "../utils/auth";
import { RouteComponentProps } from "src/declaration";
import { RouteBuilder } from "./router";
import { ErrorBoundary, LocationProvider } from "preact-iso";
import * as Sentry from "@sentry/react";

const FORCE_DOOR_PUBLIC = false;

export default class App extends Component {
  pages(pagesList: pageEntry[], currentUser?: loggedInUser): pageList {
    if (currentUser?.claims?.door_public || FORCE_DOOR_PUBLIC) {
      return {
        "/": {
          component: "DoorPublic",
          title: "Door (Barcodes)",
          topLevelApp: true,
          fullscreen: true,
        },
      };
    }

    const viewablePages = pagesList.filter((page) => {
      return (
        page.component &&
        (!page.role ||
          (page.role === "person" && currentUser) ||
          (page.role === "browser" && typeof window !== "undefined") ||
          (currentUser?.claims &&
            (currentUser?.claims[page.role] || currentUser?.claims.admin)))
      );
    });

    return Object.fromEntries(viewablePages.map((x) => [x.url, x]));
  }

  headerLinks(pages: pageList) {
    return Object.keys(pages)
      .filter((page) => pages[page].menuOrder)
      .sort((a, b) => (pages[a].menuOrder || 0) - (pages[b].menuOrder || 0))
      .map((page) => {
        const component = pages[page].component;
        if (component in prerenderMenu) {
          return {
            label: pages[page].title,
            subMenu: prerenderMenu[component as keyof typeof prerenderMenu],
          } as menuItemContent;
        }
        return { label: pages[page].title, url: page } as menuItemContent;
      });
  }

  addPagesFromFirestore(
    claims: claimSet,
    setPages: React.Dispatch<React.SetStateAction<pageList>>,
    newUser?: loggedInUser
  ) {
    // if editor/admin, then check firestore for any new pages
    const pages = this.pages(prerenderData as pageEntry[], newUser);
    if ((claims.admin || claims.editor) && !FORCE_DOOR_PUBLIC) {
      const q = query(collection(db, "html-published"));
      getDocs(q).then((querySnapshot) => {
        querySnapshot.docs.forEach((doc) => {
          const url = decodeURIComponent(doc.id);
          if (!(url in pages)) {
            pages[url] = { component: "", title: "", ...doc.data() };
          }
        });
      });
    }
    setPages(pages);
  }

  render() {
    const [currentUser, setCurrentUser] = useState<loggedInUser>();

    const [pages, setPages] = useState<pageList>(
      this.pages(prerenderData as pageEntry[], currentUser)
    );

    const cleanPath = (path: string) => {
      const parts = path
        .trim()
        .replace(/^\W+|\W+$/g, "")
        .toLocaleLowerCase()
        .split(/\W+/g);
      const length = parts.length;
      return Array.from({ length }, (_, i) => {
        return parts.slice(0, i + 1).join("-");
      }).join(" ");
    };

    const [pathClass, setPathClass] = useState(
      typeof window !== "undefined" ? cleanPath(window.location.pathname) : ""
    );
    useEffect(() => {
      if (typeof window !== "undefined" && pathClass === undefined) {
        setPathClass(cleanPath(window.location.pathname));
      }
    }, []);

    const auth = getAuth();
    auth.onAuthStateChanged((user) => {
      if (user && auth.currentUser) {
        auth.currentUser
          .getIdTokenResult()
          .then((idTokenResult) => {
            const claimSet = toClaimSet(idTokenResult.claims);
            const changed =
              currentUser?.name !== auth.currentUser?.displayName ||
              !claimSetEquivalent(claimSet, currentUser?.claims || {});
            if (changed) {
              const newUser: loggedInUser = {
                name: auth.currentUser?.displayName,
                email: auth.currentUser?.email,
                emailVerified: auth?.currentUser?.emailVerified,
                claims: claimSet,
              };
              Sentry.setUser({
                email: newUser.email || undefined,
                username: newUser.name || undefined,
              });
              setCurrentUser(newUser);
              this.addPagesFromFirestore(claimSet, setPages, newUser);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        // not logged in
        Sentry.setUser(null);
        setCurrentUser(undefined);
      }
    });

    getRedirectResult(auth)
      .then((result) => result && postLoginGoogle(result))
      .catch((err) => console.error("auth redirect error: ", err));

    if (
      typeof window !== "undefined" &&
      isSignInWithEmailLink(auth, window.location.href)
    ) {
      if (auth?.currentUser?.emailVerified) {
        // Already logged in. Might have reloaded page with URL params
        return;
      }
      const email =
        window.localStorage.getItem("emailForSignIn") ||
        window.prompt("Please provide your email address for confirmation");
      if (!email) {
        return;
      }
      signInWithEmailLink(auth, email, window.location.href)
        .then((result) => result && postLoginToken(result))
        .catch((err) => console.error("auth token error: ", err));
    }

    if (Object.keys(pages).length === 1) {
      const path = Object.keys(pages)[0];
      const page = pages[path];
      if (page.fullscreen) {
        const component = Controllers[page.component];
        const props = {
          path,
          currentUser,
        } as RouteComponentProps;
        return component(props);
      }
    }

    return (
      <div id="app" class={pathClass}>
        <Header
          links={this.headerLinks(pages)}
          currentUser={currentUser}
          heading="Ceroc &amp; Modern Jive Adelaide"
          shortHeading="CMJA: Ceroc Adelaide"
        />
        <div class="app-body-wrap">
          <div class="app-body">
            <div class="hide-if-top-level">
              <Announcements />
              <img
                src="https://firebasestorage.googleapis.com/v0/b/ceroc-website.appspot.com/o/Ceroc%20Logo.webp?alt=media&token=6fcc4c50-377c-4e73-985e-cbe2479157f2"
                alt="Ceroc & Modern Jive Adelaide logo"
                class="cerocLogo"
              />
            </div>
            <LocationProvider>
              <ErrorBoundary>
                <RouteBuilder
                  pages={pages}
                  currentUser={currentUser}
                  onChange={(path) => setPathClass(cleanPath(path))}
                />
              </ErrorBoundary>
            </LocationProvider>
          </div>
        </div>
        <Footer links={this.headerLinks(pages)} currentUser={currentUser} />
      </div>
    );
  }
}
